import React, { useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Container,
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Typography } from "@mui/material";

interface HeaderProps {
  searchBar?: React.ReactNode;
}

const Header: React.FC<HeaderProps> = ({ searchBar }) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [utilitiesAnchorEl, setUtilitiesAnchorEl] =
    useState<null | HTMLElement>(null);

  const isActive = (path: string) => location.pathname === path;

  const navItems = [
    { label: "Home", path: "/" },
    { label: "Blog", path: "/blogs" },
    { label: "HackerNews", path: "/news" },
    { label: "Tech Stacks", path: "/tech-stacks" },
    { label: "Videos", path: "/youtube" },
    { label: "About", path: "/about" },
    { label: "Contact", path: "/contact" },
  ];

  const utilityItems = [
    { label: "Formatter Toolkit", path: "/developer-formatter-toolkit" },
    { label: "Image Compression Tool", path: "/image-compression-tool" },
    { label: "Word Counter", path: "/word-counter" },
    { label: "Password Generator", path: "/password-generator" },
    { label: "Unit Converter", path: "/unit-converter" },
    { label: "QR Generator", path: "/qr-generator" },
  ];

  const utilitiesMenuOpen = Boolean(utilitiesAnchorEl);
  const hasActiveUtility = utilityItems.some((item) => isActive(item.path));

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleOpenUtilitiesMenu = (event: React.MouseEvent<HTMLElement>) => {
    setUtilitiesAnchorEl(event.currentTarget);
  };

  const handleCloseUtilitiesMenu = () => {
    setUtilitiesAnchorEl(null);
  };

  return (
    <AppBar
      position="sticky"
      sx={{
        backgroundColor: "background.paper",
        borderBottom: "1px solid",
        borderColor: "divider",
        boxShadow: "none",
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ minHeight: "64px !important" }}>
          {/* Site Logo / Name */}
          <Box
            component={RouterLink}
            to="/"
            sx={{
              textDecoration: "none",
              display: "flex",
              alignItems: "center",
              mr: 2,
              flexShrink: 0,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "primary.main",
                letterSpacing: "-0.5px",
                fontSize: { xs: "1rem", sm: "1.15rem" },
              }}
            >
              Shashi Sharma
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {searchBar && (
            <Box
              sx={{
                flexGrow: 1,
                maxWidth: 500,
                mx: 3,
                display: { xs: "none", sm: "block" },
              }}
            >
              {searchBar}
            </Box>
          )}

          {/* Desktop Navigation */}
          <Box sx={{ display: { xs: "none", md: "flex" }, gap: 1 }}>
            {navItems.map((item) => (
              <Button
                key={item.path}
                component={RouterLink}
                to={item.path}
                sx={{
                  color: isActive(item.path) ? "primary.main" : "text.primary",
                  fontWeight: isActive(item.path) ? 600 : 400,
                  "&:hover": {
                    backgroundColor: "rgba(145, 189, 58, 0.08)",
                  },
                }}
              >
                {item.label}
              </Button>
            ))}

            <Button
              id="utilities-menu-button"
              onClick={handleOpenUtilitiesMenu}
              endIcon={<ExpandMoreIcon />}
              aria-controls={utilitiesMenuOpen ? "utilities-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={utilitiesMenuOpen ? "true" : undefined}
              sx={{
                color: hasActiveUtility ? "primary.main" : "text.primary",
                fontWeight: hasActiveUtility ? 600 : 400,
                "&:hover": {
                  backgroundColor: "rgba(145, 189, 58, 0.08)",
                },
              }}
            >
              Utilities
            </Button>
            <Menu
              id="utilities-menu"
              anchorEl={utilitiesAnchorEl}
              open={utilitiesMenuOpen}
              onClose={handleCloseUtilitiesMenu}
              MenuListProps={{ "aria-labelledby": "utilities-menu-button" }}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              {utilityItems.map((item) => (
                <MenuItem
                  key={item.path}
                  component={RouterLink}
                  to={item.path}
                  onClick={handleCloseUtilitiesMenu}
                  selected={isActive(item.path)}
                >
                  {item.label}
                </MenuItem>
              ))}
            </Menu>
          </Box>

          {/* Mobile Hamburger Menu */}
          <IconButton
            sx={{ display: { xs: "flex", md: "none" }, color: "text.primary" }}
            onClick={toggleMobileMenu}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </Container>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={toggleMobileMenu}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: 250,
            backgroundColor: "background.paper",
          },
        }}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ fontWeight: 700, color: "text.primary" }}>Menu</Box>
          <IconButton onClick={toggleMobileMenu} sx={{ color: "text.primary" }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <List>
          {navItems.map((item) => (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                component={RouterLink}
                to={item.path}
                onClick={toggleMobileMenu}
                sx={{
                  backgroundColor: isActive(item.path)
                    ? "rgba(145, 189, 58, 0.12)"
                    : "transparent",
                  "&:hover": {
                    backgroundColor: "rgba(145, 189, 58, 0.08)",
                  },
                }}
              >
                <ListItemText
                  primary={item.label}
                  sx={{
                    color: isActive(item.path)
                      ? "primary.main"
                      : "text.primary",
                    fontWeight: isActive(item.path) ? 600 : 400,
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}

          <Box
            sx={{
              px: 2,
              pt: 2,
              pb: 1,
              color: "text.secondary",
              fontSize: "0.75rem",
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            Utilities
          </Box>
          {utilityItems.map((item) => (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                component={RouterLink}
                to={item.path}
                onClick={toggleMobileMenu}
                sx={{
                  pl: 3,
                  backgroundColor: isActive(item.path)
                    ? "rgba(145, 189, 58, 0.12)"
                    : "transparent",
                  "&:hover": {
                    backgroundColor: "rgba(145, 189, 58, 0.08)",
                  },
                }}
              >
                <ListItemText
                  primary={item.label}
                  sx={{
                    color: isActive(item.path)
                      ? "primary.main"
                      : "text.primary",
                    fontWeight: isActive(item.path) ? 600 : 400,
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>
    </AppBar>
  );
};

export default Header;
